import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import {
  LucideAngularModule,
  Users,
  ChartBar,
  Shield,
  CreditCard,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  UserPlus,
  Menu,
  X,
  Settings,
  ClipboardList,
  Megaphone,
  Store,
  Download,
  Smartphone,
} from 'lucide-angular';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-admin-shell',
  standalone: true,
  imports: [CommonModule, NgClass, RouterModule, LucideAngularModule, TranslatePipe],
  template: `
    <div class="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      <!-- Mobile top bar -->
      <div
        class="lg:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 flex items-center px-4 gap-3"
      >
        <button
          (click)="mobileSidebarOpen.set(true)"
          class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <lucide-icon
            [img]="MenuIcon"
            class="w-5 h-5 text-gray-700 dark:text-gray-300"
          ></lucide-icon>
        </button>
        <div class="flex items-center gap-2">
          <div
            class="w-7 h-7 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg flex items-center justify-center"
          >
            <lucide-icon [img]="Shield" class="w-3.5 h-3.5 text-white"></lucide-icon>
          </div>
          <span class="font-bold text-gray-900 dark:text-white text-sm">Admin Panel</span>
        </div>
      </div>

      <!-- Desktop sidebar -->
      <aside
        class="hidden lg:flex w-64 flex-col shrink-0 bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800"
      >
        <div class="h-16 flex items-center px-6 border-b border-gray-200 dark:border-gray-800">
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg flex items-center justify-center"
            >
              <lucide-icon [img]="Shield" class="w-4 h-4 text-white"></lucide-icon>
            </div>
            <span class="font-bold text-gray-900 dark:text-white">Admin Panel</span>
          </div>
        </div>
        <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
          @for (item of navItems(); track item.label) {
            <a
              [routerLink]="item.route"
              routerLinkActive="bg-green-50 dark:bg-green-900/40 text-green-600 dark:text-green-400"
              [routerLinkActiveOptions]="{ exact: item.route === '/admin' }"
              class="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm font-medium"
            >
              <lucide-icon [img]="item.icon" class="w-4 h-4 shrink-0"></lucide-icon>
              <span>{{ item.label | translate }}</span>
            </a>
          }
        </nav>
        <div class="p-4 border-t border-gray-200 dark:border-gray-800 space-y-2">
          <div class="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ 'ADMIN.SHELL.LOGGED_IN' | translate }}
            </p>
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
              {{ authService.user()?.fullName }}
            </p>
            <span class="text-xs px-2 py-0.5 rounded-full font-medium" [class]="roleClass()">{{
              authService.user()?.role
            }}</span>
          </div>
          <button
            (click)="authService.logout()"
            class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm"
          >
            <lucide-icon [img]="LogOut" class="w-4 h-4"></lucide-icon>
            <span>{{ 'ADMIN.SHELL.LOGOUT' | translate }}</span>
          </button>
        </div>
      </aside>

      <!-- Mobile sidebar overlay -->
      @if (mobileSidebarOpen()) {
        <div class="fixed inset-0 z-50 lg:hidden">
          <!-- eslint-disable-next-line @angular-eslint/template/elements-content -->
          <button class="fixed inset-0 bg-black/50" (click)="mobileSidebarOpen.set(false)"></button>
          <aside
            class="fixed inset-y-0 left-0 w-64 flex flex-col bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800"
          >
            <div
              class="h-14 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-800"
            >
              <div class="flex items-center gap-2">
                <div
                  class="w-7 h-7 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg flex items-center justify-center"
                >
                  <lucide-icon [img]="Shield" class="w-3.5 h-3.5 text-white"></lucide-icon>
                </div>
                <span class="font-bold text-gray-900 dark:text-white text-sm">Admin Panel</span>
              </div>
              <button
                (click)="mobileSidebarOpen.set(false)"
                class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <lucide-icon [img]="XIcon" class="w-5 h-5 text-gray-500"></lucide-icon>
              </button>
            </div>
            <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
              @for (item of navItems(); track item.label) {
                <a
                  [routerLink]="item.route"
                  routerLinkActive="bg-green-50 dark:bg-green-900/40 text-green-600 dark:text-green-400"
                  [routerLinkActiveOptions]="{ exact: item.route === '/admin' }"
                  (click)="mobileSidebarOpen.set(false)"
                  class="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm font-medium"
                >
                  <lucide-icon [img]="item.icon" class="w-4 h-4 shrink-0"></lucide-icon>
                  <span>{{ item.label | translate }}</span>
                </a>
              }
            </nav>
            <div class="p-4 border-t border-gray-200 dark:border-gray-800 space-y-2">
              <div class="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900">
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ 'ADMIN.SHELL.LOGGED_IN' | translate }}
                </p>
                <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {{ authService.user()?.fullName }}
                </p>
              </div>
              <button
                (click)="authService.logout()"
                class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm"
              >
                <lucide-icon [img]="LogOut" class="w-4 h-4"></lucide-icon>
                <span>{{ 'ADMIN.SHELL.LOGOUT' | translate }}</span>
              </button>
            </div>
          </aside>
        </div>
      }

      <!-- Main content -->
      <main class="flex-1 overflow-auto pt-14 pb-20 lg:pt-0 lg:pb-0">
        <router-outlet></router-outlet>
      </main>

      <!-- Mobile Bottom Navigation -->
      <nav
        class="fixed bottom-0 left-0 right-0 z-30 lg:hidden bg-white/95 dark:bg-gray-950/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-800"
        style="padding-bottom: env(safe-area-inset-bottom)"
      >
        <div class="flex h-16">
          @for (item of bottomNavItems(); track item.route) {
            <a
              [routerLink]="item.route"
              routerLinkActive
              [routerLinkActiveOptions]="{ exact: item.route === '/admin' }"
              #rla="routerLinkActive"
              class="flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors duration-200 relative"
              [ngClass]="
                rla.isActive
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400'
              "
            >
              <span
                class="absolute top-0 w-8 h-[2.5px] rounded-b-full transition-all duration-300"
                [ngClass]="rla.isActive ? 'bg-green-500' : 'bg-transparent'"
              ></span>
              <div
                class="flex items-center justify-center w-9 h-7 rounded-xl transition-all duration-200"
                [ngClass]="rla.isActive ? 'bg-green-50 dark:bg-green-950/50' : ''"
              >
                <lucide-icon [img]="item.icon" class="w-5 h-5 block shrink-0"></lucide-icon>
              </div>
              <span class="text-[10px] font-medium leading-none tracking-wide">
                {{ item.label | translate }}
              </span>
            </a>
          }
        </div>
      </nav>
    </div>
  `,
})
export class AdminShellComponent {
  authService = inject(AuthService);
  mobileSidebarOpen = signal(false);

  Shield = Shield;
  LogOut = LogOut;
  MenuIcon = Menu;
  XIcon = X;
  Settings = Settings;
  Megaphone = Megaphone;
  Store = Store;
  Smartphone = Smartphone;

  roleClass = computed(() => {
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  });

  navItems = computed(() => {
    const role = this.authService.user()?.role;
    const items = [
      { label: 'ADMIN.SHELL.MAIN', route: '/admin', icon: LayoutDashboard },
      { label: 'ADMIN.SHELL.CUSTOMERS', route: '/admin/customers', icon: Users },
      { label: 'Arizalar', route: '/admin/lead-applications', icon: ClipboardList },
      { label: 'Feedbacklar', route: '/admin/feedbacks', icon: MessageSquare },
      { label: 'ADMIN.SHELL.ANALYTICS', route: '/admin/analytics', icon: ChartBar },
    ];
    if (role === 'SuperAdmin') {
      items.push(
        { label: 'ADMIN.SHELL.STAFF', route: '/admin/staff', icon: UserPlus },
        { label: 'ADMIN.SHELL.PLANS', route: '/admin/subscription-plans', icon: CreditCard },
        { label: "Do'kon turlari", route: '/admin/store-types', icon: Store },
        { label: 'Xabarnomalar', route: '/admin/announcements', icon: Megaphone },
        { label: 'Xabar matnlari', route: '/admin/notification-templates', icon: MessageSquare },
        { label: 'POS versiyalari', route: '/admin/app-releases', icon: Download },
        { label: 'SMS xizmati', route: '/admin/sms-gateway', icon: Smartphone },
      );
    }
    items.push({ label: 'ADMIN.SHELL.SETTINGS', route: '/app/settings', icon: Settings });
    return items;
  });

  bottomNavItems = computed(() => {
    const role = this.authService.user()?.role;
    const items = [
      { label: 'ADMIN.SHELL.MAIN', route: '/admin', icon: LayoutDashboard },
      { label: 'ADMIN.SHELL.CUSTOMERS', route: '/admin/customers', icon: Users },
      { label: 'Arizalar', route: '/admin/lead-applications', icon: ClipboardList },
      { label: 'Feedbacklar', route: '/admin/feedbacks', icon: MessageSquare },
      { label: 'ADMIN.SHELL.ANALYTICS', route: '/admin/analytics', icon: ChartBar },
    ];
    if (role === 'SuperAdmin') {
      items.push({ label: 'ADMIN.SHELL.STAFF', route: '/admin/staff', icon: UserPlus });
    } else {
      items.push({ label: 'ADMIN.SHELL.SETTINGS', route: '/app/settings', icon: Settings });
    }
    return items;
  });
}
